import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ExternalLink,
  BarChart3,
  Factory,
  Leaf,
  Scale,
  TrendingUp,
  Users,
  Building2,
  Route,
  Boxes,
  Gavel,
} from "lucide-react";
import entregablesData from "@/data/entregables.json";

const iconMap = {
  BarChart3,
  Factory,
  Leaf,
  Scale,
  TrendingUp,
  Users,
  Building2,
  Route,
  Boxes,
  Gavel,
};

export default function EntregableDetalle() {
  // SEO Component for Work Packages
  const { wpId } = useParams();
  const navigate = useNavigate();

  const wpIndex = entregablesData.findIndex((wp) => wp.id === wpId);
  const wp = entregablesData[wpIndex];

  if (!wp) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">
            Entregable no encontrado
          </h1>
          <Link
            to="/entregables"
            className="text-[#22c55e] hover:underline"
          >
            Volver a Entregables
          </Link>
        </div>
      </div>
    );
  }

  const IconComponent = iconMap[wp.icono] || BarChart3;
  const prevWp = wpIndex > 0 ? entregablesData[wpIndex - 1] : null;
  const nextWp = wpIndex < entregablesData.length - 1 ? entregablesData[wpIndex + 1] : null;

  // JSON-LD structured data
  const reportSchema = {
    "@context": "https://schema.org",
    "@type": "Report",
    "@id": `https://www.sostrebalear.com/entregables/${wp.id}`,
    "name": `WP${wp.numero}: ${wp.titulo}`,
    "description": wp.subtitulo,
    "author": { "@type": "Organization", "name": "PASTORIA PROJECT SL", "url": "https://www.pastoria.es" },
    "datePublished": "2025-12-01",
    "inLanguage": "es",
    "isPartOf": {
      "@type": "ResearchProject",
      "name": "SOSTRE BALEAR",
      "url": "https://www.sostrebalear.com"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [{
      "@type": "Question",
      "name": wp.pregunta,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": wp.conclusiones.join(" ")
      }
    }]
  };

  // Stringify JSON-LD once
  const reportSchemaStr = JSON.stringify(reportSchema);
  const faqSchemaStr = JSON.stringify(faqSchema);

  return (
    <div className="min-h-screen pt-20" data-testid={`entregable-detalle-${wpId}`}>
      <Helmet
        title={"WP" + wp.numero + ": " + wp.titulo + " | SOSTRE BALEAR"}
      >
        <meta name="description" content={wp.conclusiones[0]} />
        <link rel="canonical" href={"https://www.sostrebalear.com/entregables/" + wp.id} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={"WP" + wp.numero + ": " + wp.titulo + " | SOSTRE BALEAR"} />
        <meta property="og:description" content={wp.pregunta} />
        <meta property="og:url" content={"https://www.sostrebalear.com/entregables/" + wp.id} />
        <meta property="og:image" content="https://www.sostrebalear.com/assets/logos/sostre-balear.png" />
        <meta property="og:locale" content="es_ES" />
        <meta property="og:site_name" content="SOSTRE BALEAR" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={"WP" + wp.numero + ": " + wp.titulo + " | SOSTRE BALEAR"} />
        <meta name="twitter:description" content={wp.pregunta} />
      </Helmet>
      {/* JSON-LD Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: reportSchemaStr }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: faqSchemaStr }} />
      {/* Header */}
      <section
        className="py-16 md:py-24"
        style={{
          background: `linear-gradient(to bottom right, ${wp.color}08, ${wp.color}03, white)`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <button
            onClick={() => navigate("/entregables")}
            className="inline-flex items-center gap-2 text-slate-600 hover:text-[#22c55e] transition-colors mb-8"
          >
            <ArrowLeft size={20} />
            Volver a Entregables
          </button>

          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-4 mb-6">
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center"
                  style={{ backgroundColor: `${wp.color}15` }}
                >
                  <IconComponent size={32} style={{ color: wp.color }} />
                </div>
                <div>
                  <span className="text-sm font-medium text-slate-500">
                    Work Package {wp.numero}
                  </span>
                  <h1 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
                    {wp.titulo}
                  </h1>
                </div>
              </div>

              <p className="text-lg text-slate-600 mb-8">{wp.subtitulo}</p>

              {/* Pregunta clave */}
              <div
                className="bg-white rounded-2xl p-6 border-l-4 shadow-sm"
                style={{ borderColor: wp.color }}
              >
                <span className="text-sm font-medium text-slate-500 uppercase tracking-wide">
                  Pregunta Clave
                </span>
                <p className="text-xl font-semibold text-slate-900 mt-2">
                  {wp.pregunta}
                </p>
              </div>
            </div>

            <div className="lg:col-span-4">
              <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
                <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-4">
                  Navegación Rápida
                </h3>
                <div className="space-y-2">
                  {entregablesData.map((item) => (
                    <Link
                      key={item.id}
                      to={`/entregables/${item.id}`}
                      className={`block px-4 py-2 rounded-lg text-sm transition-colors ${
                        item.id === wpId
                          ? "bg-[#22c55e] text-white font-medium"
                          : "text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      WP{item.numero}: {item.titulo}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Conclusiones */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8">
            Conclusiones Principales
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            {wp.conclusiones.map((conclusion, index) => (
              <div
                key={index}
                className="bg-slate-50 rounded-2xl p-6 border border-slate-100"
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm mb-4"
                  style={{ backgroundColor: wp.color }}
                >
                  {index + 1}
                </div>
                <p className="text-slate-700 leading-relaxed">{conclusion}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Implicaciones */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
              Implicaciones
            </h2>
            <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm">
              <div className="flex items-start gap-4">
                <CheckCircle2
                  size={24}
                  className="shrink-0 mt-1"
                  style={{ color: wp.color }}
                />
                <p className="text-lg text-slate-700 leading-relaxed">
                  {wp.implicaciones}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Normativa Relacionada */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">
              Normativa Relacionada
            </h2>
            <Link
              to={`/normativa?wp=${wpId}`}
              className="inline-flex items-center gap-2 text-[#22c55e] hover:underline font-medium"
              data-testid="link-normativa"
            >
              Ver toda la normativa
              <ExternalLink size={16} />
            </Link>
          </div>
          <p className="text-slate-600 mb-6">
            Consulta la normativa aplicable relacionada con este entregable.
          </p>
          <Link
            to={`/normativa?wp=${wpId}`}
            className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 px-6 py-3 rounded-full font-medium transition-colors"
          >
            Filtrar normativa por {wp.titulo}
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* Navegación */}
      <section className="py-8 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            {prevWp ? (
              <Link
                to={`/entregables/${prevWp.id}`}
                className="flex items-center gap-3 text-slate-600 hover:text-[#22c55e] transition-colors"
              >
                <ArrowLeft size={20} />
                <div className="text-left">
                  <span className="text-xs text-slate-400">Anterior</span>
                  <p className="font-medium">WP{prevWp.numero}: {prevWp.titulo}</p>
                </div>
              </Link>
            ) : (
              <div />
            )}

            {nextWp ? (
              <Link
                to={`/entregables/${nextWp.id}`}
                className="flex items-center gap-3 text-slate-600 hover:text-[#22c55e] transition-colors text-right"
              >
                <div>
                  <span className="text-xs text-slate-400">Siguiente</span>
                  <p className="font-medium">WP{nextWp.numero}: {nextWp.titulo}</p>
                </div>
                <ArrowRight size={20} />
              </Link>
            ) : (
              <div />
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
